import React from 'react';

// This version uses CKEDITOR from the global variable.
// IoTextArea bundles the full editor into the builds.
export function Richtext({
    className,
    html,
    placeholder,
    height = null,
    toolbar = null,
    allowedContent = null,
    onChange
}) {
    // Avoid CKEditor issues with rerendering component by rendering once.
    // Just update the properties on the persistent wrapper div instead.
    const wrapperRef = React.useRef(null);
    const [wrapper] = React.useState(() => <div ref={wrapperRef}></div>);

    const onChangeRef = React.useRef(null);
    onChangeRef.current = onChange;

    // Remember if the HTML has change.
    const htmlRef = React.useRef(null);
    const htmlChanged = htmlRef.current !== html;
    htmlRef.current = html;

    const editorRef = React.useRef(null);
    const editorHtmlRef = React.useRef(null);

    // Only init editor once, inside wrapper.
    React.useEffect(() => {
        const textarea = document.createElement('textarea');
        wrapperRef.current.appendChild(textarea);
        textarea.value = editorHtmlRef.current = htmlRef.current;
        const config = {};
        if (placeholder) {
            // Placeholder can only be set once on init.
            config.placeholder = placeholder;
        }
        if (toolbar) {
            config.toolbar = toolbar;
        }
        if (allowedContent) {
            config.allowedContent = allowedContent;
        }
        if (height) {
            config.height = height;
        }
        const editor = CKEDITOR.replace(textarea, config);
        window.cke = editor;
        editor.on('change', e => {
            // Only fire change events if the HTML changed.
            const html = e.editor.getData();
            if (html !== editorHtmlRef.current) {
                htmlRef.current = editorHtmlRef.current = html;
                if (onChangeRef.current) {
                    onChangeRef.current(html);
                }
            }
        });
        editorRef.current = editor;
        return () => {
            editor.destroy();
        };
    }, [wrapper]);

    // Update the persistent wrapper element on any changes.
    React.useEffect(() => {
        wrapperRef.current.className = className;

        // Only set data if actually different.
        if (! htmlChanged) {
            return;
        }

        // The initial HTML may be formatted after calling setData.
        // After setData capture any initial change at contentDom.
        // Then update the value compared againts on the change events.
        editorHtmlRef.current = html;
        const onContentDom = e => {
            editorHtmlRef.current = e.editor.getData();
        };
        editorRef.current.once('contentDom', onContentDom);
        editorRef.current.setData(htmlRef.current);
        return () => {
            editorRef.current.removeListener('contentDom', onContentDom);
        };
    });

    return wrapper;
}
